import React, {useEffect, useMemo, useState} from "react";
import {useNavigate, useOutletContext, useParams, useSearchParams} from "react-router-dom";
import Button from "../../../../../components/Button";
import api from "../../../../../lib/api";
import {notify} from "../../../../../components/Notifications";
import {getErrMsg} from "../../../../../lib/errors";
import {useBusiness} from "../../../../../context/BusinessContext";
import {CheckIcon, CopyIcon, PencilIcon, PlusIcon, SaveIcon, Trash2Icon, XIcon} from "lucide-react";
import SectionHeading from "../../../../../components/SectionHeading";
import {TicketScopePicker, type TicketKind, type TicketOption} from "../components/TicketScopePicker";

type OutletCtx = { eventId: string | null };

type CommissionType = "PERCENT" | "FIXED" | "TIERED";

/** One band of a TIERED commission: the percentage applies to all paid tickets once minTickets are paid. */
type CommissionTierDto = { minTickets: number; commissionPercent: number };
type CommissionTierRow = { minTickets: string; commissionPercent: string };

// Mirrors the backend's PromoterCommissionTiers.MAX_TIERS.
const MAX_TIERS = 20;
type DiscountType = "PERCENT" | "FIXED";

type PromoterDto = {
    id: string;
    code: string;

    commissionType: CommissionType;
    commissionValue: number;
    /** TIERED bands, ascending; empty for PERCENT and FIXED. */
    commissionTiers?: CommissionTierDto[] | null;
    currency?: string | null;
    active: boolean;

    /** Public event link carrying this promoter's code; null until the event has a slug. */
    shareUrl?: string | null;

    userId?: string | null;
    userName?: string | null;
    userEmail?: string | null;

    hasDiscount?: boolean;
    couponId?: string | null;

    createdAt?: string | null;
    updatedAt?: string | null;
};

// Linked coupon shape (only the fields we need to prefill the discount form in edit mode).
type CouponDto = {
    id: string;
    discountType: DiscountType;
    discountValue: number;
    currency?: string | null;
    maxTotalUses?: number | null;
    maxUsesPerCustomer?: number | null;
    validFrom?: string | null;
    validUntil?: string | null;
    minOrderAmount?: number | null;
    ticketTypeIds?: string[] | null;
    description?: string | null;
};

type PromoterForm = {
    email: string;
    code: string;

    commissionType: CommissionType;
    commissionValue: string;
    tiers: CommissionTierRow[];
    currency: string;

    active: boolean;

    // optional linked buyer discount
    discountEnabled: boolean;
    discountType: DiscountType;
    discountValue: string;
    maxTotalUses: string;        // "" => unlimited
    maxUsesPerCustomer: string;
    validFrom: string;           // datetime-local
    validUntil: string;
    minOrderAmount: string;
    ticketTypeIds: string[];     // empty => all ticket types
    description: string;
};

const cx = (...c: Array<string | false | null | undefined>) => c.filter(Boolean).join(" ");

function toNumber(v: string): number {
    const n = Number((v ?? "").replace(/[^\d.]/g, ""));
    return Number.isFinite(n) ? n : 0;
}

function toIntOrNull(v: string): number | null {
    const s = (v ?? "").trim();
    if (!s) return null; // treat empty as unlimited
    const n = Number(s.replace(/[^\d]/g, ""));
    if (!Number.isFinite(n)) return null;
    return Math.max(0, Math.trunc(n));
}

function normalizeCode(code: string) {
    return (code ?? "")
        .toUpperCase()
        .replace(/\s+/g, "")
        .replace(/[^A-Z0-9_-]/g, "");
}

function isValidEmail(v: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((v ?? "").trim());
}

function toLocalInputValue(iso?: string | null) {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(v?: string | null) {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

function formatTiers(tiers: CommissionTierDto[]) {
    return [...tiers]
        .sort((a, b) => a.minTickets - b.minTickets)
        .map((t, i) => (i === 0 ? `${t.commissionPercent}%` : `${t.commissionPercent}% from ${t.minTickets} tickets`))
        .join(", ");
}

const SHARE_BASE = "https://share.nam-events.com";

/** The backend's link moved onto the share domain, like the app's other share links; null without a link. */
function shareUrlFor(p: PromoterDto): string | null {
    if (!p.shareUrl) return null;
    try {
        const u = new URL(p.shareUrl);
        return `${SHARE_BASE}${u.pathname}${u.search}`;
    } catch {
        return p.shareUrl;
    }
}

function CopyLinkButton({value}: { value: string }) {
    const [copied, setCopied] = useState(false);

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
        } catch {
            notify.error("Could not copy the link");
        }
    }

    return (
        <button
            type="button"
            onClick={handleCopy}
            aria-label="Copy promoter link"
            title="Copy promoter link"
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
        >
            {copied ? <CheckIcon className="size-3.5 text-green-600"/> : <CopyIcon className="size-3.5"/>}
            {copied ? "Copied" : "Copy"}
        </button>
    );
}

function formatCommission(p: PromoterDto) {
    if (p.commissionType === "TIERED") return `Tiered commission: ${formatTiers(p.commissionTiers ?? [])}`;
    if (p.commissionType === "PERCENT") return `${Math.round(p.commissionValue)}% commission`;
    const cur = p.currency || "NAD";
    return `${cur} ${Number(p.commissionValue || 0).toFixed(2)} per ticket`;
}

function emptyForm(): PromoterForm {
    return {
        email: "",
        code: "",
        commissionType: "PERCENT",
        commissionValue: "10",
        tiers: [],
        currency: "NAD",
        active: true,
        discountEnabled: false,
        discountType: "PERCENT",
        discountValue: "10",
        maxTotalUses: "",
        maxUsesPerCustomer: "",
        validFrom: "",
        validUntil: "",
        minOrderAmount: "",
        ticketTypeIds: [],
        description: "",
    };
}

function formFromDto(p: PromoterDto, coupon: CouponDto | null): PromoterForm {
    const base: PromoterForm = {
        ...emptyForm(),
        email: p.userEmail ?? "",
        code: p.code ?? "",
        // Keep TIERED as it is: mapping it to PERCENT would turn the promoter flat on the first save.
        commissionType: p.commissionType === "FIXED" || p.commissionType === "TIERED" ? p.commissionType : "PERCENT",
        commissionValue: String(p.commissionValue ?? 0),
        tiers: [...(p.commissionTiers ?? [])]
            .sort((a, b) => a.minTickets - b.minTickets)
            .map((t) => ({minTickets: String(t.minTickets), commissionPercent: String(t.commissionPercent)})),
        currency: String(p.currency ?? "NAD"),
        active: Boolean(p.active),
        discountEnabled: Boolean(p.hasDiscount),
    };

    if (coupon) {
        base.discountType = coupon.discountType === "FIXED" ? "FIXED" : "PERCENT";
        base.discountValue = String(coupon.discountValue ?? 0);
        base.maxTotalUses = coupon.maxTotalUses == null ? "" : String(coupon.maxTotalUses);
        base.maxUsesPerCustomer = coupon.maxUsesPerCustomer == null ? "" : String(coupon.maxUsesPerCustomer);
        base.validFrom = toLocalInputValue(coupon.validFrom ?? null);
        base.validUntil = toLocalInputValue(coupon.validUntil ?? null);
        base.minOrderAmount = coupon.minOrderAmount == null ? "" : String(coupon.minOrderAmount);
        base.ticketTypeIds = Array.isArray(coupon.ticketTypeIds) ? coupon.ticketTypeIds.map((x) => String(x)) : [];
        base.description = String(coupon.description ?? "");
    }

    return base;
}

export function PromotersStep({mode}: { mode: "create" | "edit" }) {
    const {activeBusiness} = useBusiness();
    const businessId = activeBusiness?.id ?? null;

    const navigate = useNavigate();
    const params = useParams();
    const [sp] = useSearchParams();
    const outlet = useOutletContext<OutletCtx>();

    const eventId =
        mode === "edit"
            ? (params.eventId ?? outlet.eventId ?? null)
            : (outlet.eventId ?? sp.get("eventId") ?? null);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [promoters, setPromoters] = useState<PromoterDto[]>([]);
    const [ticketOptions, setTicketOptions] = useState<TicketOption[]>([]);
    const [couponsById, setCouponsById] = useState<Record<string, CouponDto>>({});

    // modal state
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [form, setForm] = useState<PromoterForm>(emptyForm());

    const load = async () => {
        if (!businessId || !eventId) return;

        setLoading(true);
        try {
            const {data} = await api.get(`/api/v1/business/${businessId}/events/${eventId}/promoters`);
            const list: PromoterDto[] = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : []);
            setPromoters(list);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to load promoters"));
        } finally {
            setLoading(false);
        }

        try {
            const tRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/tickets`);
            const raw = Array.isArray(tRes?.data) ? tRes.data : (tRes?.data?.tickets ?? []);
            setTicketOptions(
                (raw ?? [])
                    .slice()
                    .sort((a: any, b: any) => (a?.position ?? 0) - (b?.position ?? 0))
                    .map((t: any) => ({
                        id: String(t?.id ?? ""),
                        name: t?.name ?? "Untitled",
                        kind: (t?.kind ?? "TICKET") as TicketKind,
                    }))
                    .filter((t: TicketOption) => t.id)
            );
        } catch {
            setTicketOptions([]);
        }

        // Linked discounts live as coupons; keep a lookup so we can prefill the discount form in edit mode.
        try {
            const cRes = await api.get(`/api/v1/business/${businessId}/events/${eventId}/coupons`);
            const list: CouponDto[] = Array.isArray(cRes?.data) ? cRes.data : (Array.isArray(cRes?.data?.items) ? cRes.data.items : []);
            const map: Record<string, CouponDto> = {};
            for (const c of list) if (c?.id) map[String(c.id)] = c;
            setCouponsById(map);
        } catch {
            setCouponsById({});
        }
    };

    useEffect(() => {
        if (!businessId || !eventId) return;
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessId, eventId]);

    const openCreate = () => {
        setEditingId(null);
        setForm(emptyForm());
        setOpen(true);
    };

    const openEdit = (p: PromoterDto) => {
        setEditingId(p.id);
        const coupon = p.couponId ? (couponsById[String(p.couponId)] ?? null) : null;
        setForm(formFromDto(p, coupon));
        setOpen(true);
    };

    const closeModal = () => {
        setOpen(false);
        setEditingId(null);
        setForm(emptyForm());
    };

    const codeNorm = useMemo(() => normalizeCode(form.code), [form.code]);

    const commissionValueNum = useMemo(() => {
        const v = toNumber(form.commissionValue);
        if (form.commissionType === "PERCENT") return Math.min(100, Math.max(0, v));
        return Math.max(0, v);
    }, [form.commissionValue, form.commissionType]);

    const tiersParsed = useMemo(
        () => form.tiers.map((t) => ({
            minTickets: toIntOrNull(t.minTickets),
            commissionPercent: toNumber(t.commissionPercent),
        })),
        [form.tiers]
    );

    const discountValueNum = useMemo(() => {
        const v = toNumber(form.discountValue);
        if (form.discountType === "PERCENT") return Math.min(100, Math.max(0, v));
        return Math.max(0, v);
    }, [form.discountValue, form.discountType]);

    const maxTotalUsesNum = useMemo(() => toIntOrNull(form.maxTotalUses), [form.maxTotalUses]);
    const maxUsesPerCustomerNum = useMemo(() => toIntOrNull(form.maxUsesPerCustomer), [form.maxUsesPerCustomer]);

    const minOrderAmountNum = useMemo(() => {
        const s = (form.minOrderAmount ?? "").trim();
        if (!s) return null;
        return Math.max(0, toNumber(s));
    }, [form.minOrderAmount]);

    const validation = useMemo(() => {
        // email only required/editable when enlisting a new promoter
        if (!editingId) {
            if (!form.email.trim()) return {ok: false, msg: "Promoter email is required."};
            if (!isValidEmail(form.email)) return {ok: false, msg: "Enter a valid promoter email."};
        }

        // unique code check (case-insensitive) against existing promoters
        if (codeNorm) {
            const clash = promoters.find((p) => normalizeCode(p.code) === codeNorm && p.id !== editingId);
            if (clash) return {ok: false, msg: `Code "${codeNorm}" already exists for this event.`};
        }

        if (form.commissionType === "TIERED") {
            if (!tiersParsed.length) return {ok: false, msg: "Add at least one commission tier."};
            if (tiersParsed.length > MAX_TIERS) return {ok: false, msg: `At most ${MAX_TIERS} commission tiers.`};
            const seen = new Set<number>();
            for (const t of tiersParsed) {
                if (t.minTickets == null || t.minTickets < 1) {
                    return {ok: false, msg: "Each tier needs a ticket count of at least 1."};
                }
                if (seen.has(t.minTickets)) return {ok: false, msg: `Two tiers start at ${t.minTickets} tickets.`};
                seen.add(t.minTickets);
                if (t.commissionPercent <= 0 || t.commissionPercent > 100) {
                    return {ok: false, msg: "Each tier's commission must be above 0% and at most 100%."};
                }
            }
            if (!seen.has(1)) return {ok: false, msg: "One tier must start at 1 ticket."};
        } else if (commissionValueNum <= 0) {
            return {ok: false, msg: "Commission value must be greater than 0."};
        }

        if (form.discountEnabled) {
            if (discountValueNum <= 0) return {ok: false, msg: "Discount value must be greater than 0."};

            if (maxTotalUsesNum != null && maxUsesPerCustomerNum != null && maxUsesPerCustomerNum > maxTotalUsesNum) {
                return {ok: false, msg: "Max uses per customer cannot exceed max total uses."};
            }

            const vf = fromLocalInputValue(form.validFrom);
            const vu = fromLocalInputValue(form.validUntil);
            if (vf && vu && new Date(vf).getTime() > new Date(vu).getTime()) {
                return {ok: false, msg: "Valid until must be after valid from."};
            }
        }

        return {ok: true, msg: ""};
    }, [
        editingId,
        form.email,
        codeNorm,
        promoters,
        commissionValueNum,
        form.commissionType,
        tiersParsed,
        form.discountEnabled,
        discountValueNum,
        maxTotalUsesNum,
        maxUsesPerCustomerNum,
        form.validFrom,
        form.validUntil,
    ]);

    const buildDiscount = () => ({
        discountType: form.discountType,
        discountValue: discountValueNum,
        currency: form.currency ?? "NAD",
        maxTotalUses: maxTotalUsesNum,
        maxUsesPerCustomer: maxUsesPerCustomerNum,
        validFrom: fromLocalInputValue(form.validFrom),
        validUntil: fromLocalInputValue(form.validUntil),
        minOrderAmount: minOrderAmountNum,
        ticketTypeIds: form.ticketTypeIds,
        description: form.description ?? "",
    });

    // TIERED sends its bands and no flat value; the backend derives commissionValue from the first tier.
    const buildCommission = () => form.commissionType === "TIERED"
        ? {
            commissionType: form.commissionType,
            tiers: tiersParsed
                .map((t) => ({minTickets: t.minTickets as number, commissionPercent: t.commissionPercent}))
                .sort((a, b) => a.minTickets - b.minTickets),
        }
        : {commissionType: form.commissionType, commissionValue: commissionValueNum};

    const setCommissionType = (type: CommissionType) => {
        setForm((p) => ({
            ...p,
            commissionType: type,
            // Start a fresh tier list from the current percentage, so switching does not lose it.
            tiers: type === "TIERED" && !p.tiers.length
                ? [{minTickets: "1", commissionPercent: p.commissionType === "PERCENT" ? p.commissionValue : "10"}]
                : p.tiers,
        }));
    };

    const updateTier = (index: number, patch: Partial<CommissionTierRow>) => {
        setForm((p) => ({...p, tiers: p.tiers.map((t, i) => (i === index ? {...t, ...patch} : t))}));
    };

    const addTier = () => {
        setForm((p) => {
            const last = p.tiers[p.tiers.length - 1];
            const lastMin = last ? (toIntOrNull(last.minTickets) ?? 1) : 0;
            return {
                ...p,
                tiers: [...p.tiers, {
                    minTickets: String(last ? lastMin + 50 : 1),
                    commissionPercent: last?.commissionPercent ?? "10",
                }],
            };
        });
    };

    const removeTier = (index: number) => {
        setForm((p) => ({...p, tiers: p.tiers.filter((_, i) => i !== index)}));
    };

    const savePromoter = async () => {
        if (!businessId || !eventId) return;

        if (!validation.ok) {
            notify.error(validation.msg || "Please fix the form errors.");
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                const payload = {
                    code: codeNorm || null,
                    ...buildCommission(),
                    active: form.active,
                    // null leaves an existing discount unchanged; providing it creates/updates.
                    discount: form.discountEnabled ? buildDiscount() : null,
                };
                await api.put(
                    `/api/v1/business/${businessId}/events/${eventId}/promoters/${encodeURIComponent(editingId)}`,
                    payload
                );
                notify.success("Promoter updated");
            } else {
                const payload = {
                    email: form.email.trim(),
                    code: codeNorm || null,
                    ...buildCommission(),
                    discount: form.discountEnabled ? buildDiscount() : null,
                };
                await api.post(`/api/v1/business/${businessId}/events/${eventId}/promoters`, payload);
                notify.success("Promoter added");
            }

            closeModal();
            await load();
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to save promoter"));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (p: PromoterDto) => {
        if (!businessId || !eventId) return;

        setSaving(true);
        try {
            await api.put(`/api/v1/business/${businessId}/events/${eventId}/promoters/${encodeURIComponent(p.id)}`, {
                active: !p.active,
            });
            setPromoters((prev) => prev.map((x) => (x.id === p.id ? {...x, active: !p.active} : x)));
            notify.success(`Promoter ${!p.active ? "activated" : "deactivated"}`);
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to update promoter"));
        } finally {
            setSaving(false);
        }
    };

    const deletePromoter = async (p: PromoterDto) => {
        if (!businessId || !eventId) return;

        // eslint-disable-next-line no-alert
        if (!window.confirm(`Remove promoter "${p.userName || p.userEmail || normalizeCode(p.code)}"?`)) return;

        setSaving(true);
        try {
            await api.delete(`/api/v1/business/${businessId}/events/${eventId}/promoters/${encodeURIComponent(p.id)}`);
            setPromoters((prev) => prev.filter((x) => x.id !== p.id));
            notify.success("Promoter removed");
        } catch (e) {
            notify.error(getErrMsg(e, "Failed to remove promoter"));
        } finally {
            setSaving(false);
        }
    };

    const sortedPromoters = useMemo(() => {
        return [...promoters].sort((a, b) => {
            const av = a.active ? 0 : 1;
            const bv = b.active ? 0 : 1;
            if (av !== bv) return av - bv;
            const an = (a.userName || a.userEmail || a.code || "").toLowerCase();
            const bn = (b.userName || b.userEmail || b.code || "").toLowerCase();
            return an.localeCompare(bn);
        });
    }, [promoters]);

    const goNext = () => {
        if (!eventId) return;
        if (mode === "edit") navigate(`/business/events/${encodeURIComponent(eventId)}/edit/refund-policy`);
        else navigate(`/business/events/new/refund-policy?eventId=${encodeURIComponent(eventId)}`);
    };

    if (!eventId) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600">
                Please complete <span className="font-medium">Event Details</span> first.
            </div>
        );
    }

    return (
        <div className="space-y-6">

            <SectionHeading
                title={mode === "edit" ? "Manage Promoters" : "Add Promoters"}
                description="Enlist existing NamEvents users to promote this event. Each promoter gets a share code, earns a commission on attributed sales, and can optionally give buyers a discount when their code is used."
                actions={
                    <>
                        <Button type="button" variant="primary" size="md" onClick={() => openCreate()}
                                disabled={saving}>
                            <PlusIcon className="size-4"/>
                            New promoter
                        </Button>
                    </>
                }
            />

            {loading ? (
                <div className="py-10 text-sm text-gray-500">Loading promoters…</div>
            ) : (
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm">
                    <div className="border-b border-gray-200 p-6">
                        <div className="text-sm font-semibold text-gray-900">Existing promoters</div>
                        <p className="mt-1 text-sm text-gray-600">
                            {sortedPromoters.length ? `${sortedPromoters.length} promoter(s)` : "No promoters yet."}
                        </p>
                    </div>

                    {sortedPromoters.length ? (
                        <div className="divide-y divide-gray-100">
                            {sortedPromoters.map((p) => {
                                const statusPill = p.active
                                    ? {t: "Active", cls: "bg-green-50 text-green-700"}
                                    : {t: "Inactive", cls: "bg-amber-50 text-amber-800"};
                                const link = shareUrlFor(p);

                                return (
                                    <div key={p.id} className="p-6">
                                        <div
                                            className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                            <div className="min-w-0">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <div className="text-lg font-semibold text-gray-900">
                                                        {p.userName || p.userEmail || "Promoter"}
                                                    </div>
                                                    <span
                                                        className={cx("rounded-full px-2.5 py-1 text-xs font-semibold", statusPill.cls)}>
                                                        {statusPill.t}
                                                    </span>
                                                    {p.hasDiscount ? (
                                                        <span
                                                            className="rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                                                            Buyer discount
                                                        </span>
                                                    ) : null}
                                                </div>

                                                <div className="mt-1 text-sm text-gray-600">
                                                    <span className="font-medium">Code: {normalizeCode(p.code)}</span>
                                                    <span className="mx-2 text-gray-300">•</span>
                                                    <span>{formatCommission(p)}</span>
                                                </div>

                                                {p.userEmail ? (
                                                    <div className="mt-2 text-xs text-gray-500">{p.userEmail}</div>
                                                ) : null}

                                                {link ? (
                                                    <div className="mt-3 flex max-w-xl items-center gap-2 rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                                                        <a href={link} target="_blank" rel="noopener noreferrer"
                                                           className="min-w-0 flex-1 truncate text-sm text-gray-700 hover:underline">
                                                            {link}
                                                        </a>
                                                        <CopyLinkButton value={link}/>
                                                    </div>
                                                ) : null}
                                            </div>

                                            <div className="flex shrink-0 flex-col gap-2 sm:items-end">
                                                <label className="inline-flex items-center gap-3">
                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(p.active)}
                                                        onChange={() => toggleActive(p)}
                                                        disabled={saving}
                                                        className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                                    />
                                                    <span
                                                        className={cx("text-sm font-semibold", p.active ? "text-gray-900" : "text-gray-500")}>
                                                        {p.active ? "Active" : "Inactive"}
                                                    </span>
                                                </label>

                                                <div className="flex gap-2">
                                                    <Button type="button" variant="secondary" size="md"
                                                            onClick={() => openEdit(p)} disabled={saving}>
                                                        <span className="inline-flex items-center gap-2">
                                                            <PencilIcon className="size-4"/>
                                                            Edit
                                                        </span>
                                                    </Button>
                                                    <Button type="button" variant="danger" size="md"
                                                            onClick={() => deletePromoter(p)} disabled={saving}>
                                                        <span className="inline-flex items-center gap-2">
                                                            <Trash2Icon className="size-4"/>
                                                            Remove
                                                        </span>
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="p-6 text-sm text-gray-600">
                            No promoters added yet. Click <span className="font-medium">New promoter</span> to add
                            one.
                        </div>
                    )}
                </div>
            )}

            <div className="flex items-center justify-end gap-3">
                <Button type="button" variant="secondary" size="md"
                        onClick={() => navigate("/business/events")}>
                    Exit
                </Button>
                <Button type="button" variant="primary" size="md" onClick={goNext} disabled={saving}>
                    Continue to Refund Policy
                </Button>
            </div>

            {/* Modal */}
            {open ? (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/30" onClick={closeModal}/>
                    <div
                        className="relative flex max-h-[calc(100vh-2rem)] w-full max-w-2xl flex-col rounded-2xl border border-gray-200 bg-white shadow-xl">
                        <div className="flex shrink-0 items-start justify-between border-b border-gray-200 p-6">
                            <div>
                                <div className="text-xl font-semibold text-gray-900">
                                    {editingId ? "Edit promoter" : "New promoter"}
                                </div>
                                <p className="mt-1 text-sm text-gray-600">
                                    Configure the promoter, their commission and an optional buyer discount.
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={closeModal}
                                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                            >
                                <XIcon className="size-5"/>
                            </button>
                        </div>

                        <div className="min-h-0 flex-1 overflow-y-auto p-6">
                            {!validation.ok ? (
                                <div
                                    className="mb-4 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                                    {validation.msg}
                                </div>
                            ) : null}

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="sm:col-span-2">
                                    <label className="block text-sm font-medium text-gray-900">Promoter email</label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {editingId
                                            ? "The linked NamEvents user cannot be changed."
                                            : "Email of the existing NamEvents user to enlist as promoter."}
                                    </p>
                                    <input
                                        disabled={Boolean(editingId)}
                                        value={form.email}
                                        onChange={(e) => setForm((p) => ({...p, email: e.target.value}))}
                                        type="email"
                                        inputMode="email"
                                        placeholder="name@example.com"
                                        className={cx(
                                            "mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none",
                                            editingId && "bg-gray-50 text-gray-500"
                                        )}
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Share code
                                        (optional)</label>
                                    <p className="mt-1 text-xs text-gray-500">Auto-generated when blank (A–Z, 0–9, - ,
                                        _)</p>
                                    <input
                                        value={form.code}
                                        onChange={(e) => setForm((p) => ({...p, code: e.target.value}))}
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">
                                        Normalized: <span className="font-medium">{codeNorm || "auto"}</span>
                                    </p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-900">Commission type</label>
                                    <p className="mt-1 text-xs text-gray-500">Percent of subtotal, flat per
                                        ticket, or a percent that rises with tickets sold</p>
                                    <select
                                        value={form.commissionType}
                                        onChange={(e) => setCommissionType(e.target.value as CommissionType)}
                                        className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    >
                                        <option value="PERCENT">Percentage (%)</option>
                                        <option value="FIXED">Fixed per ticket</option>
                                        <option value="TIERED">Tiered by tickets sold (%)</option>
                                    </select>
                                </div>

                                {form.commissionType === "TIERED" ? (
                                    <div className="sm:col-span-2 rounded-xl border border-gray-200 p-4">
                                        <div className="text-sm font-semibold text-gray-900">Commission tiers</div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            The tier is chosen by the promoter&apos;s total paid tickets for this event,
                                            and its percentage applies to all of them. Example: 7% from 1 ticket and 10%
                                            from 50 means the 50th paid ticket lifts every ticket to 10%. Refunds can
                                            move a promoter back down.
                                        </p>
                                        <div className="mt-3 space-y-2">
                                            {form.tiers.map((t, i) => (
                                                <div key={i} className="flex items-end gap-2">
                                                    <label className="flex-1">
                                                        <span className="block text-xs font-medium text-gray-700">From ticket</span>
                                                        <input
                                                            value={t.minTickets}
                                                            onChange={(e) => updateTier(i, {minTickets: e.target.value})}
                                                            inputMode="numeric"
                                                            aria-label={`Tier ${i + 1} starts at ticket`}
                                                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                                        />
                                                    </label>
                                                    <label className="flex-1">
                                                        <span className="block text-xs font-medium text-gray-700">Commission (%)</span>
                                                        <input
                                                            value={t.commissionPercent}
                                                            onChange={(e) => updateTier(i, {commissionPercent: e.target.value})}
                                                            inputMode="decimal"
                                                            aria-label={`Tier ${i + 1} commission percent`}
                                                            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                                        />
                                                    </label>
                                                    <button
                                                        type="button"
                                                        onClick={() => removeTier(i)}
                                                        disabled={form.tiers.length <= 1}
                                                        aria-label={`Remove tier ${i + 1}`}
                                                        className="mb-0.5 rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700 disabled:opacity-40"
                                                    >
                                                        <Trash2Icon className="size-4"/>
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="mt-3">
                                            <Button type="button" variant="secondary" size="sm" onClick={addTier}
                                                    disabled={form.tiers.length >= MAX_TIERS}>
                                                <span className="inline-flex items-center gap-2">
                                                    <PlusIcon className="size-4"/>
                                                    Add tier
                                                </span>
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                <div>
                                    <label className="block text-sm font-medium text-gray-900">
                                        Commission value {form.commissionType === "PERCENT" ? "(%)" : "(amount)"}
                                    </label>
                                    <p className="mt-1 text-xs text-gray-500">
                                        {form.commissionType === "PERCENT" ? "Example: 10 = 10% of subtotal" : "Example: 5 = N$5 per ticket"}
                                    </p>
                                    <input
                                        value={form.commissionValue}
                                        onChange={(e) => setForm((p) => ({...p, commissionValue: e.target.value}))}
                                        inputMode="decimal"
                                        className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                    />
                                    <p className="mt-2 text-xs text-gray-500">
                                        Current:{" "}
                                        <span className="font-medium">
                                            {form.commissionType === "PERCENT"
                                                ? `${commissionValueNum.toFixed(0)}%`
                                                : `N$${commissionValueNum.toFixed(2)}`}
                                        </span>
                                    </p>
                                </div>
                                )}

                                {editingId ? (
                                    <div className="sm:col-span-2 flex items-center justify-between rounded-xl border border-gray-200 p-4">
                                        <div className="min-w-0">
                                            <div className="text-sm font-semibold text-gray-900">Active</div>
                                            <p className="mt-1 text-sm text-gray-600">Inactive promoters stop earning
                                                commission and their code is disabled.</p>
                                        </div>
                                        <label className="inline-flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={form.active}
                                                onChange={(e) => setForm((p) => ({...p, active: e.target.checked}))}
                                                className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                            />
                                            <span
                                                className={cx("text-sm font-semibold", form.active ? "text-gray-900" : "text-gray-500")}>
                                                {form.active ? "Active" : "Inactive"}
                                            </span>
                                        </label>
                                    </div>
                                ) : null}
                            </div>

                            {/* Optional buyer discount */}
                            <div className="mt-6 rounded-xl border border-gray-200 p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0">
                                        <div className="text-sm font-semibold text-gray-900">Attach a buyer
                                            discount</div>
                                        <p className="mt-1 text-sm text-gray-600">
                                            When enabled, buyers using this promoter's code get a discount. The discount
                                            code is the promoter's share code.
                                        </p>
                                    </div>
                                    <label className="inline-flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={form.discountEnabled}
                                            onChange={(e) => setForm((p) => ({...p, discountEnabled: e.target.checked}))}
                                            className="size-5 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                        />
                                        <span
                                            className={cx("text-sm font-semibold", form.discountEnabled ? "text-gray-900" : "text-gray-500")}>
                                            {form.discountEnabled ? "On" : "Off"}
                                        </span>
                                    </label>
                                </div>

                                {form.discountEnabled ? (
                                    <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Discount
                                                type</label>
                                            <select
                                                value={form.discountType}
                                                onChange={(e) => setForm((p) => ({
                                                    ...p,
                                                    discountType: e.target.value as DiscountType
                                                }))}
                                                className="mt-2 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            >
                                                <option value="PERCENT">Percentage (%)</option>
                                                <option value="FIXED">Fixed value</option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">
                                                Discount value {form.discountType === "PERCENT" ? "(%)" : "(amount)"}
                                            </label>
                                            <input
                                                value={form.discountValue}
                                                onChange={(e) => setForm((p) => ({...p, discountValue: e.target.value}))}
                                                inputMode="decimal"
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                            <p className="mt-2 text-xs text-gray-500">
                                                Current:{" "}
                                                <span className="font-medium">
                                                    {form.discountType === "PERCENT"
                                                        ? `${discountValueNum.toFixed(0)}%`
                                                        : `N$${discountValueNum.toFixed(2)}`}
                                                </span>
                                            </p>
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Min order amount
                                                (optional)</label>
                                            <input
                                                value={form.minOrderAmount}
                                                onChange={(e) => setForm((p) => ({
                                                    ...p,
                                                    minOrderAmount: e.target.value
                                                }))}
                                                inputMode="decimal"
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Max total uses
                                                (blank = unlimited)</label>
                                            <input
                                                value={form.maxTotalUses}
                                                onChange={(e) => setForm((p) => ({...p, maxTotalUses: e.target.value}))}
                                                inputMode="numeric"
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Max uses per
                                                customer (blank = unlimited)</label>
                                            <input
                                                value={form.maxUsesPerCustomer}
                                                onChange={(e) => setForm((p) => ({
                                                    ...p,
                                                    maxUsesPerCustomer: e.target.value
                                                }))}
                                                inputMode="numeric"
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Valid from
                                                (optional)</label>
                                            <input
                                                type="datetime-local"
                                                value={form.validFrom}
                                                onChange={(e) => setForm((p) => ({...p, validFrom: e.target.value}))}
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-900">Valid until
                                                (optional)</label>
                                            <input
                                                type="datetime-local"
                                                value={form.validUntil}
                                                onChange={(e) => setForm((p) => ({...p, validUntil: e.target.value}))}
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label className="block text-sm font-medium text-gray-900">Apply to ticket
                                                types</label>
                                            <p className="mt-1 text-xs text-gray-500">
                                                Leave "Applies to all tickets" on, or turn it off to restrict this
                                                discount to specific ticket types.
                                            </p>
                                            <TicketScopePicker
                                                label="Apply this discount"
                                                ticketTypeIds={form.ticketTypeIds}
                                                tickets={ticketOptions}
                                                onChange={(ids) => setForm((p) => ({...p, ticketTypeIds: ids}))}
                                            />
                                        </div>

                                        <div className="sm:col-span-2">
                                            <label className="block text-sm font-medium text-gray-900">Customer message
                                                (optional)</label>
                                            <textarea
                                                rows={3}
                                                value={form.description}
                                                onChange={(e) => setForm((p) => ({...p, description: e.target.value}))}
                                                className="mt-2 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-600 focus:outline-none"
                                            />
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>

                        <div className="flex shrink-0 items-center justify-between border-t border-gray-200 p-6">
                            <Button type="button" variant="secondary" size="md" onClick={closeModal}
                                    disabled={saving}>
                                Cancel
                            </Button>
                            <Button type="button" variant="primary" size="md" onClick={savePromoter}
                                    disabled={saving}>
                                <span className="inline-flex items-center gap-2">
                                    <SaveIcon className="size-4"/>
                                    {editingId ? "Save changes" : "Add promoter"}
                                </span>
                            </Button>
                        </div>
                    </div>
                </div>
            ) : null}
        </div>
    );
}
